import { Inject, Injectable, Optional } from '@angular/core';
import { IHttpConnectionOptions } from '@microsoft/signalr';
import { Store } from '@ngxs/store';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { INimbusSignalRHub } from '@nimbus-global/features/signalr/i-nimbus-signalr.hub';
import { CallRecordChangedNotification, CallRecordNotification } from '@nimbus-global/features/signalr/call-record-notification.model';
import { SIGNALR_DIAGNOSTIC_CLIENT } from '@nimbus-global/features/signalr/signalR.const';
import { ForkRequestsService } from '@nimbus-global/features/shared/fork-requests.service';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { IServiceSessionLive } from '@nimbus-global/features/reportingLiveData/models/service-session.model';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IClientSideLogger, LogLevel } from '@nimbus-library/features/logger/services/client-logger/i-client-side-logger';
import { WallboardSelectors } from '../../store/wallboard.selectors';
import { WallboardAuthenticationService } from '../infrastructure/wallboard-authentication.service';

@Injectable({ providedIn: 'root' })
export class NimbusWallboardSignalRHub extends INimbusSignalRHub {
  private keyAddToGroupsWithTeamId: string = 'AddToGroupsWithTeamId';
  private keyRemoveFromGroupsWithTeamId: string = 'RemoveFromGroupsWithTeamId';
  private keyServiceSessionsUpdated: string = 'ServiceSessionsUpdated';
  private keyCallRecordCreated: string = 'CallRecordCreated';
  private keyCallRecordChanged: string = 'CallRecordChanged';

  protected signalRUrl: string = 'api/v2/wallboards/signalr-hub/v1/wallboard-hub';

  public constructor(
    private readonly store: Store,
    authenticationService: IAuthenticationService,
    forkRequestService: ForkRequestsService,
    @Optional() @Inject(SIGNALR_DIAGNOSTIC_CLIENT) signalRDiagnosticClient: boolean,
    @Optional() clientSideLogger: IClientSideLogger,
    private readonly wallboardAuthenticationService: WallboardAuthenticationService,
  ) {
    super(authenticationService, forkRequestService, signalRDiagnosticClient, clientSideLogger);
    this.initialize(this.getIsReadyToConnect$());
  }

  public addToGroupsWithTeamId<TResult>(teamIds: string[], nimbusPermission: NimbusPermission): Observable<TResult> {
    return this.invokeBatchWithArguments(this.keyAddToGroupsWithTeamId, teamIds, nimbusPermission);
  }

  public removeFromGroupsWithTeamId<TResult>(teamIds: string[], nimbusPermission: NimbusPermission): Observable<TResult> {
    return this.invokeBatchWithArguments(this.keyRemoveFromGroupsWithTeamId, teamIds, nimbusPermission);
  }

  protected getIsReadyToConnect$(): Observable<boolean> {
    return combineLatest([
      this.authenticationService.isAuthenticated$,
      this.store.select(WallboardSelectors.bootstrap).pipe(map((bootstrap) => !!bootstrap)),
      this.wallboardAuthenticationService.isWallboardNotFound$,
    ]).pipe(
      map(([isAuthenticated, isBootstrapped, isWallboardNotFound]) => !!isAuthenticated && isBootstrapped && !isWallboardNotFound),
      distinctUntilChanged(),
    );
  }

  protected acquireIdToken(): Observable<string> {
    return of(undefined);
  }

  protected override requiresIdToken(): boolean {
    return false;
  }

  protected override getConnectionOptions(): IHttpConnectionOptions {
    return { withCredentials: true };
  }

  protected subscribeEvents(): void {
    this.subscribeEvent(this.keyServiceSessionsUpdated, this.serviceSessionsUpdated.bind(this));
    this.subscribeEvent(this.keyCallRecordCreated, this.callRecordCreated.bind(this));
    this.subscribeEvent(this.keyCallRecordChanged, this.callRecordChanged.bind(this));
  }

  protected unsubscribeEvents(): void {
    this.unsubscribeEvent(this.keyServiceSessionsUpdated);
    this.unsubscribeEvent(this.keyCallRecordCreated);
    this.unsubscribeEvent(this.keyCallRecordChanged);
  }

  private serviceSessionsUpdated(data: IServiceSessionLive[]): void {
    this.clientSideLogger?.writeMessage(
      LogLevel.Info,
      'SignalR',
      `SignalR Event. EventName: ServiceSessionUpdated: ${JSON.stringify(
        data.map((d) => ({
          id: d.conversationId,
          unifiedConversationId: d.unifiedConversationId,
          state: d.state,
          orderNumber: d.orderNumber,
        })),
      )}`,
    );
    this.onServiceSessionsUpdated$.next(data);
  }

  private callRecordCreated(data: CallRecordNotification): void {
    this.onCallRecordCreated$.next(data);
  }

  private callRecordChanged(data: CallRecordChangedNotification): void {
    this.onCallRecordChanged$.next(data);
  }
}
